import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useBeforeUnload, useBlocker } from 'react-router';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ui/confirm';

type Draft = { dirty: boolean; busy: boolean; scope?: string };
type Registry = {
  drafts: Map<string, Draft>;
  register: (id: string, draft: Draft | null) => void;
  allowSignOut: () => void;
  allowDocumentNavigation: () => void;
};
const RegistryContext = createContext<Registry | null>(null);
const ScopeContext = createContext<string | undefined>(undefined);

/** One navigation blocker for the app, even when multiple editors are open in a drawer. */
export function DraftProvider({ children }: { children: ReactNode }) {
  const [drafts, setDrafts] = useState(new Map<string, Draft>());
  const confirm = useConfirm();
  const signOutApproved = useRef(false);
  const documentNavigationApproved = useRef(false);
  const allowDocumentNavigation = useCallback(() => {
    documentNavigationApproved.current = true;
  }, []);
  const allowSignOut = useCallback(() => {
    signOutApproved.current = true;
  }, []);
  const register = useCallback((id: string, draft: Draft | null) => {
    setDrafts((previous) => {
      const next = new Map(previous);
      if (draft) next.set(id, draft);
      else next.delete(id);
      return next;
    });
  }, []);
  const busy = [...drafts.values()].some((d) => d.busy);
  const dirty = [...drafts.values()].some((d) => d.dirty);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (signOutApproved.current && nextLocation.pathname === '/login') {
      signOutApproved.current = false;
      return false;
    }
    return (dirty || busy) && currentLocation.pathname !== nextLocation.pathname;
  });
  const asked = useRef(false);
  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if ((dirty || busy) && !documentNavigationApproved.current) {
          event.preventDefault();
          event.returnValue = '';
        }
      },
      [dirty, busy],
    ),
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') {
      asked.current = false;
      return;
    }
    if (busy) {
      blocker.reset();
      toast.info('Wait for your changes to finish saving.');
      return;
    }
    if (asked.current) return;
    asked.current = true;
    confirm({
      title: 'Discard unsaved changes?',
      description:
        'Your changes haven’t been saved. Stay here to continue editing, or discard them to leave.',
      confirmLabel: 'Discard changes',
      cancelLabel: 'Keep editing',
      action: () => blocker.proceed(),
      onCancel: () => blocker.reset(),
    });
  }, [blocker, busy, confirm]);
  return (
    <RegistryContext.Provider value={{ drafts, register, allowSignOut, allowDocumentNavigation }}>
      {children}
    </RegistryContext.Provider>
  );
}

export function DraftScope({ id, children }: { id: string; children: ReactNode }) {
  return <ScopeContext.Provider value={id}>{children}</ScopeContext.Provider>;
}

/** Protect a single editor or a drawer containing several independently saved editors. */
export function useDraftGuard(
  dirty: boolean,
  busy = false,
  scopeOverride?: string,
  documentNavigation = false,
) {
  const registry = useContext(RegistryContext);
  const inheritedScope = useContext(ScopeContext);
  const scope = scopeOverride ?? inheritedScope;
  const id = useId();
  const confirm = useConfirm();
  const register = registry?.register;
  useEffect(() => {
    register?.(id, { dirty, busy, scope });
    return () => register?.(id, null);
  }, [register, id, dirty, busy, scope]);
  return (action: () => void) => {
    const proceed = () => {
      if (documentNavigation) registry?.allowDocumentNavigation();
      return action();
    };
    const siblings = scopeOverride
      ? [...(registry?.drafts.values() ?? [])].filter(
          (d) => scopeOverride === '*' || d.scope === scopeOverride,
        )
      : [];
    if (busy || siblings.some((d) => d.busy)) {
      toast.info('Please wait for the current action to finish.');
      return;
    }
    if (!dirty && !siblings.some((d) => d.dirty)) {
      proceed();
      return;
    }
    confirm({
      title: 'Discard unsaved changes?',
      description: 'Your changes haven’t been saved.',
      confirmLabel: 'Discard changes',
      cancelLabel: 'Keep editing',
      action: () => {
        // The auth event unmounts editors before its redirect. Consent covers that redirect too.
        if (scopeOverride === '*') registry?.allowSignOut();
        return proceed();
      },
    });
  };
}
